import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Avatar,
  Grid,
  Divider,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  Container,
  Popover,
  Collapse,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import YouTubeIcon from "@mui/icons-material/YouTube";
import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import PaymentIcon from "@mui/icons-material/Payment";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getDoctorById } from "../../services/doctorService";
import { getAllStaff } from "../../services/StaffService";
import { isPathDeskRole, isPathModuleStaffRole } from "../../utils/pathRoleUtils";
import {
  getAppointmentsByDoctorAndDate,
  getAppointmentsByPatientId,
  createPatientPortalAppointment,
  updateAppointment,
} from "../../services/appointmentService";
import { getPatientByUserId, getPatientById, syncPatientTypeForWorkflow } from "../../services/patientService";
import { getAllHospitalOrganizations, getHospitalOrganizationById } from "../../services/hospitalOrganizationService";
import { useAuth } from "../../context/AuthContext";
import { createInvoice } from "../../services/invoiceService";
import { toast } from "react-toastify";
import {
  scheduleRemindersForAppointment,
  rescheduleAppointmentReminders,
} from "../../services/appointmentReminderService";
import { getAllDepartment } from "../../services/departmentService";
import { getPrescriptionById } from "../../services/prescriptionService";
import { bookPrescribedLabTests } from "../../services/patientLabBookingService";
import {
  describeLabBookingCheck,
  validatePrescriptionForLabBooking,
} from "./appointmentPatientUtils";
import { LoginModal } from "../patientLogin";
import PatientAddProfile, { isPatientProfileIncomplete } from "./PatientAddProfile";
import {
  savePendingAppointment,
  readPendingAppointment,
  clearPendingAppointment,
  clearBookingOrganizationId,
  patientRecordMatchesUser,
  getBoundPatientId,
  applyPatientBookingOrganization,
  getBookingOrganizationId,
  setBookingOrganizationId,
} from "../../utils/patientPortalSession";
import { isAppointmentCompleted } from "../../utils/appointmentCancelUtils";
import {
  buildPatientAppointmentUpdatePayload,
  getAppointmentIdFromEnriched,
  getRawAppointmentDateTime,
  isAlreadyBookedAtSelectedTimeError,
  findPatientSameDoctorDayConflict,
} from "./appointmentPatientUtils";

dayjs.extend(customParseFormat);

const BLUE = "#0B78D0";
const BLUE_DARK = "#0866B3";
const BLUE_LIGHT = "#EAF4FC";
const ACCENT = BLUE;
const ACCENT_DARK = BLUE_DARK;
const HERO_BG = "#F3F4F6";
const TEXT_DARK = "#2F3A4A";
const TEXT_GREY = "#6B7280";
const BORDER = "#E5E9EF";
const DEFAULT_FEE = 500;

const parseSlotTime = (date, time) => {
  const formats = ["YYYY-MM-DD hh:mm A", "YYYY-MM-DD h:mm A", "YYYY-MM-DD HH:mm"];
  const combined = `${dayjs(date).format("YYYY-MM-DD")} ${time}`;
  const parsed = dayjs(combined, formats, true);
  if (parsed.isValid()) return parsed;
  return dayjs(`${dayjs(date).format("YYYY-MM-DD")} ${time}`, "YYYY-MM-DD hh:mm A");
};

const generateTimesBetween = (startTime, endTime) => {
  const start = dayjs(startTime, ["HH:mm", "hh:mm A", "h:mm A"]);
  const end = dayjs(endTime, ["HH:mm", "hh:mm A", "h:mm A"]);
  if (!start.isValid() || !end.isValid()) return [];
  const times = [];
  let cursor = start;
  while (cursor.isBefore(end) || cursor.isSame(end)) {
    times.push(cursor.format("hh:mm A"));
    cursor = cursor.add(20, "minute");
    if (times.length > 48) break;
  }
  return times;
};

/** Pathology desk collection slots: every hour from 9:00 AM through 5:00 PM. */
const getPathologyDeskSlots = () => {
  const times = [];
  for (let hour = 9; hour <= 17; hour += 1) {
    times.push(dayjs().hour(hour).minute(0).second(0).format("hh:mm A"));
  }
  return times;
};

const getScheduleTimings = (doctor, date) => {
  if (!doctor?.visitingSchedules || !date) return [];
  const selectedDay = dayjs(date).format("dddd").toLowerCase();
  const schedules = doctor.visitingSchedules;

  if (Array.isArray(schedules)) {
    const matches = schedules.filter((entry) => {
      const day = String(entry?.dayOfWeek || entry?.day || "").toLowerCase();
      return day === selectedDay;
    });
    return matches.flatMap((entry) => {
      if (Array.isArray(entry?.timings) && entry.timings.length) return entry.timings;
      if (Array.isArray(entry?.times) && entry.times.length) return entry.times;
      if (entry?.startTime && entry?.endTime) {
        return generateTimesBetween(entry.startTime, entry.endTime);
      }
      return [];
    });
  }

  const schedule = schedules[selectedDay] || schedules[selectedDay.slice(0, 3)];
  if (!schedule) return [];
  if (Array.isArray(schedule.timings) && schedule.timings.length) return schedule.timings;
  if (Array.isArray(schedule.times) && schedule.times.length) return schedule.times;
  if (schedule.startTime && schedule.endTime) {
    return generateTimesBetween(schedule.startTime, schedule.endTime);
  }
  return [];
};

const getHospitalName = (organization) =>
  organization?.name ??
  organization?.hospitalName ??
  organization?.hospital_name ??
  organization?.organizationName ??
  organization?.organization_name ??
  "Hospital";

const getDoctorPhoto = (doctor) =>
  doctor?.photoUrl ||
  doctor?.photo ||
  doctor?.imageUrl ||
  doctor?.image ||
  doctor?.profileImage ||
  doctor?.profilePic ||
  "";

const stripDoctorPrefix = (name) => String(name || "").replace(/^dr\.?\s+/i, "").trim();

const formatDoctorName = (name) => {
  const raw = (name || "Doctor").trim();
  return /^dr\.?\s/i.test(raw) ? raw : `Dr ${raw}`;
};

const getExperienceLabel = (doctor) => {
  if (doctor?.experience) {
    const value = String(doctor.experience).replace(/years?/i, "").trim();
    return `Exp: ${value}${/\+|yrs|years/i.test(String(doctor.experience)) ? "" : "+ yrs"}`;
  }
  if (doctor?.joiningDate) {
    const years = Math.max(0, dayjs().diff(dayjs(doctor.joiningDate), "year"));
    return years > 0 ? `Exp: ${years}+ yrs` : "";
  }
  return "";
};

const getConsultationFee = (doctor) => {
  const fee = Number(
    doctor?.consultationFee ??
      doctor?.consultation_fee ??
      doctor?.opdFee ??
      doctor?.fee ??
      DEFAULT_FEE
  );
  return Number.isFinite(fee) && fee > 0 ? fee : DEFAULT_FEE;
};

const getHospitalPhone = (organization) => {
  const raw =
    organization?.phone ??
    organization?.mobile ??
    organization?.contactNumber ??
    organization?.contact_number ??
    organization?.hospitalPhone ??
    organization?.phoneNumber ??
    organization?.phone_number ??
    "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length >= 10) {
    return digits.length === 10 ? `+91 ${digits}` : `+${digits}`;
  }
  return String(raw).trim();
};

const getDateChipLabel = (date) => {
  if (date.isSame(dayjs(), "day")) return "Today";
  if (date.isSame(dayjs().add(1, "day"), "day")) return "Tomorrow";
  return date.format("D MMM'YY");
};

const formatNextSlotLabel = (date, time) => {
  if (date.isSame(dayjs(), "day")) return `Today ${time}`;
  if (date.isSame(dayjs().add(1, "day"), "day")) return `Tomorrow ${time}`;
  return `${date.format("D MMM'YY")} ${time}`;
};

const DoctorProfile = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const appointmentTypeFromUrl = searchParams.get("type");
  const organizationIdFromUrl = searchParams.get("org");
  const prescriptionIdFromUrl = searchParams.get("prescriptionId");
  const labTestIdsQuery = searchParams.get("labTestIds") || "";
  const selectedLabTestIds = String(labTestIdsQuery)
    .split(",")
    .map((id) => Number(String(id).trim()))
    .filter((id) => Number.isFinite(id) && id > 0);
  const keepSelectedLabTests = (tests) => {
    if (!selectedLabTestIds.length) return tests;
    const allowed = new Set(selectedLabTestIds);
    return (Array.isArray(tests) ? tests : []).filter((test) =>
      allowed.has(Number(test.labTestId ?? test.id))
    );
  };
  const isVirtualFromUrl = appointmentTypeFromUrl === "virtual";
  const isInPersonFromUrl = appointmentTypeFromUrl === "in-person";
  const appointmentTypePreSelected = appointmentTypeFromUrl !== null;
  const isLabTestBooking = Boolean(prescriptionIdFromUrl);

  const [loading, setLoading] = useState(true);
  const [doctor, setDoctor] = useState(null);
  const [pathDesk, setPathDesk] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState(organizationIdFromUrl || "");
  const [appointmentDate, setAppointmentDate] = useState(dayjs());
  const [appointmentTime, setAppointmentTime] = useState("");
  const [existingAppointments, setExistingAppointments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [isVirtual, setIsVirtual] = useState(isVirtualFromUrl || false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(dayjs());
  const [paymentMethod, setPaymentMethod] = useState(isVirtualFromUrl ? "pay-online" : "pay-at-hospital");
  const [prescribedTests, setPrescribedTests] = useState([]);
  const [prescriptionReady, setPrescriptionReady] = useState(!prescriptionIdFromUrl);
  const [prescriptionError, setPrescriptionError] = useState("");
  const prescribedTestsRef = useRef([]);
  const [showLogin, setShowLogin] = useState(false);
  const [verifiedMobile, setVerifiedMobile] = useState("");
  const [showAddProfile, setShowAddProfile] = useState(false);
  const openedProfileForBookingRef = useRef(false);
  const [hospitalExpanded, setHospitalExpanded] = useState(false);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const dateStripRef = useRef(null);
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    existingAppointment: null,
    appointmentDateTime: null,
  });
  const [isRescheduling, setIsRescheduling] = useState(false);
  const allowAdditionalSameDayAppointmentRef = useRef(false);
  const rescheduleTargetRef = useRef(null);

  const bookingOrganizationId =
    doctor?.organizationId ||
    doctor?.organization_id ||
    selectedHospitalId ||
    organizationIdFromUrl ||
    getBookingOrganizationId();

  useEffect(() => {
    allowAdditionalSameDayAppointmentRef.current = false;
    setIsRescheduling(false);
    rescheduleTargetRef.current = null;
    setConfirmationDialog({
      open: false,
      existingAppointment: null,
      appointmentDateTime: null,
    });
  }, [doctorId]);

  useEffect(() => {
    if (appointmentTypePreSelected) {
      setIsVirtual(isVirtualFromUrl);
      setPaymentMethod(isVirtualFromUrl ? "pay-online" : "pay-at-hospital");
    }
  }, [appointmentTypeFromUrl, isVirtualFromUrl, appointmentTypePreSelected]);

  useEffect(() => {
    prescribedTestsRef.current = prescribedTests;
  }, [prescribedTests]);

  useEffect(() => {
    if (!prescriptionIdFromUrl) {
      setPrescribedTests([]);
      setPrescriptionError("");
      setPrescriptionReady(true);
      return;
    }
    if (!user?.id) {
      setPrescriptionReady(true);
      return;
    }
    if (!patientDetails?.id) {
      setPrescriptionReady(false);
      return;
    }

    let active = true;
    const loadPrescription = async () => {
      setPrescriptionReady(false);
      setPrescriptionError("");
      try {
        const prescription = await getPrescriptionById(prescriptionIdFromUrl);
        if (!active) return;
        const check = validatePrescriptionForLabBooking(prescription, patientDetails.id);
        if (!check.ok) {
          setPrescribedTests([]);
          setPrescriptionError(describeLabBookingCheck(check));
          return;
        }
        const chosenTests = keepSelectedLabTests(check.tests).filter((test) => test.bookable !== false);
        if (!chosenTests.length) {
          setPrescribedTests([]);
          setPrescriptionError(
            "Completed tests do not need to be booked again. Only pending tests can be booked."
          );
          return;
        }
        setPrescribedTests(chosenTests);
        setPrescriptionError("");
      } catch (error) {
        if (!active) return;
        console.error("Error loading prescription for test booking:", error);
        setPrescribedTests([]);
        setPrescriptionError(
          error?.response?.data?.message ||
            "Prescription ID is invalid or could not be verified."
        );
      } finally {
        if (active) setPrescriptionReady(true);
      }
    };

    loadPrescription();
    return () => {
      active = false;
    };
  }, [prescriptionIdFromUrl, patientDetails?.id, user?.id, labTestIdsQuery]);

  useEffect(() => {
    if (!isLabTestBooking || !selectedHospitalId) {
      setPathDesk(null);
      return undefined;
    }
    let active = true;
    const loadPathDesk = async () => {
      try {
        const staffPayload = await getAllStaff();
        const staff = Array.isArray(staffPayload)
          ? staffPayload
          : staffPayload?.data || staffPayload?.content || staffPayload?.staff || [];
        const inHospital = (Array.isArray(staff) ? staff : []).filter((person) => {
          const staffOrg = person?.organizationId ?? person?.organization_id;
          if (staffOrg == null || staffOrg === "") return true;
          return String(staffOrg) === String(selectedHospitalId);
        });
        const roleOf = (person) => person?.userRole || person?.roleName || person?.role || "";
        const desk =
          inHospital.find((person) => isPathDeskRole(roleOf(person))) ||
          inHospital.find((person) => isPathModuleStaffRole(roleOf(person))) ||
          null;
        if (active) setPathDesk(desk);
      } catch (error) {
        console.warn("Could not load pathology desk profile:", error);
        if (active) setPathDesk(null);
      }
    };
    loadPathDesk();
    return () => {
      active = false;
    };
  }, [isLabTestBooking, selectedHospitalId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!doctorId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [doctorData, departmentsData, hospitalList] = await Promise.all([
          getDoctorById(
            doctorId,
            organizationIdFromUrl || getBookingOrganizationId()
          ),
          getAllDepartment().catch(() => []),
          getAllHospitalOrganizations().catch(() => []),
        ]);
        setDoctor(doctorData);
        setDepartments(Array.isArray(departmentsData) ? departmentsData : []);
        const orgs = Array.isArray(hospitalList) ? hospitalList : [];
        setHospitals(orgs);

        const doctorOrg =
          organizationIdFromUrl ||
          doctorData?.organizationId ||
          doctorData?.organization_id ||
          getBookingOrganizationId() ||
          "";
        setSelectedHospitalId(doctorOrg ? String(doctorOrg) : "");
        if (doctorOrg) {
          setBookingOrganizationId(doctorOrg);
        }

        if (doctorOrg && !orgs.some((org) => String(org?.id ?? org?.organizationId) === String(doctorOrg))) {
          try {
            const extra = await getHospitalOrganizationById(doctorOrg);
            if (extra) setHospitals((prev) => [...prev, extra]);
          } catch (orgError) {
            console.warn("Could not load selected hospital:", orgError);
          }
        }

        if (user?.id && !verifiedMobile) {
          let patientData = null;
          try {
            patientData = await getPatientByUserId(user.id, {
              mobile: user.mobileNumber || user.username,
              organizationId:
                organizationIdFromUrl ||
                getBookingOrganizationId() ||
                user.organizationId,
            });
          } catch (lookupError) {
            patientData = null;
          }
          if (!patientRecordMatchesUser(patientData, user.id, user.mobileNumber || user.username)) {
            const boundId = getBoundPatientId(user.id);
            if (boundId) {
              try {
                patientData = await getPatientById(boundId);
              } catch (boundError) {
                patientData = null;
              }
            } else {
              patientData = null;
            }
          }
          setPatientDetails(patientData);
        } else {
          setPatientDetails(null);
        }
      } catch (err) {
        console.error("Error fetching doctor profile:", err);
        toast.error("Failed to load doctor profile. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [doctorId, user?.id, organizationIdFromUrl, verifiedMobile]);

  useEffect(() => {
    const fetchExistingAppointments = async () => {
      if (!doctorId || !selectedCalendarDate) {
        setExistingAppointments([]);
        return;
      }

      try {
        const appointments = await getAppointmentsByDoctorAndDate(
          doctorId,
          dayjs(selectedCalendarDate).format("YYYY-MM-DD"),
          bookingOrganizationId
        );
        setExistingAppointments(Array.isArray(appointments) ? appointments : []);
        setAppointmentDate(selectedCalendarDate);
        const pending = readPendingAppointment();
        const selectedDateKey = dayjs(selectedCalendarDate).format("YYYY-MM-DD");
        if (
          pending &&
          String(pending.doctorId) === String(doctorId) &&
          pending.appointmentDate === selectedDateKey &&
          pending.appointmentTime
        ) {
          setAppointmentTime(pending.appointmentTime);
        } else {
          setAppointmentTime("");
        }
      } catch (error) {
        console.error("Error fetching existing appointments:", error);
        setExistingAppointments([]);
      }
    };

    fetchExistingAppointments();
  }, [doctorId, selectedCalendarDate, bookingOrganizationId]);

  useEffect(() => {
    return () => {
      const pending = readPendingAppointment();
      if (!pending || pending.confirmOnReturn) return;
      clearPendingAppointment();
    };
  }, []);

  const persistPendingBooking = (timeOverride) => {
    savePendingAppointment({
      doctorId,
      appointmentDate: dayjs(selectedCalendarDate || appointmentDate).format("YYYY-MM-DD"),
      appointmentTime: timeOverride || appointmentTime,
      isVirtual,
      paymentMethod,
      organizationId: bookingOrganizationId,
      ...(prescriptionIdFromUrl
        ? {
            bookingKind: "lab-test",
            prescriptionId: prescriptionIdFromUrl,
            labTestIds: prescribedTestsRef.current.map((test) => test.labTestId),
          }
        : {}),
    });
  };

  const ensurePrescribedTests = async () => {
    if (!prescriptionIdFromUrl) return [];
    if (prescribedTestsRef.current.length > 0 && !prescriptionError) {
      return prescribedTestsRef.current;
    }
    const prescription = await getPrescriptionById(prescriptionIdFromUrl);
    const check = validatePrescriptionForLabBooking(prescription, patientDetails?.id);
    if (!check.ok) {
      const message = describeLabBookingCheck(check);
      setPrescribedTests([]);
      setPrescriptionError(message);
      throw new Error(message);
    }
    const chosenTests = keepSelectedLabTests(check.tests).filter((test) => test.bookable !== false);
    if (!chosenTests.length) {
      const message = "Completed tests do not need to be booked again. Only pending tests can be booked.";
      setPrescribedTests([]);
      setPrescriptionError(message);
      throw new Error(message);
    }
    prescribedTestsRef.current = chosenTests;
    setPrescribedTests(chosenTests);
    setPrescriptionError("");
    return chosenTests;
  };

  const requestLoginForBooking = (timeOverride) => {
    persistPendingBooking(timeOverride);
    setShowLogin(true);
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialog({
      open: false,
      existingAppointment: null,
      appointmentDateTime: null,
    });
  };

  const openExistingAppointmentDialog = (existingAppointment, appointmentDateTime) => {
    setConfirmationDialog({
      open: true,
      existingAppointment,
      appointmentDateTime,
    });
  };

  const lookupSameDoctorDayConflict = async (appointmentDateTime) => {
    if (!patientDetails?.id) return null;
    try {
      const appointments = await getAppointmentsByPatientId(patientDetails.id);
      return findPatientSameDoctorDayConflict({
        appointments,
        patientId: patientDetails.id,
        appointmentDateTime,
      });
    } catch (error) {
      console.error("Error checking existing patient appointments:", error);
      return null;
    }
  };

  const handleRescheduleAppointment = () => {
    const existingAppointment = confirmationDialog.existingAppointment;
    const targetAppointmentId = getAppointmentIdFromEnriched(existingAppointment);

    if (!targetAppointmentId) {
      toast.error("Cannot reschedule: Appointment ID is missing.");
      closeConfirmationDialog();
      return;
    }

    if (isAppointmentCompleted(existingAppointment)) {
      toast.error("Completed appointments cannot be rescheduled. Please create a new appointment.");
      closeConfirmationDialog();
      setIsRescheduling(false);
      rescheduleTargetRef.current = null;
      return;
    }

    rescheduleTargetRef.current = existingAppointment;
    setIsRescheduling(true);
    closeConfirmationDialog();
    toast.info("Please select a different time slot, then click Update Appointment.");
  };

  const handleCreateNewAppointmentChoice = () => {
    allowAdditionalSameDayAppointmentRef.current = true;
    setIsRescheduling(false);
    rescheduleTargetRef.current = null;
    closeConfirmationDialog();
    toast.info("Please select a different time slot to create another appointment.");
  };

  const handleBookAppointment = async () => {
    if (!user?.id) {
      if (!appointmentTime) {
        toast.error("Please select a time slot.");
        return;
      }
      requestLoginForBooking();
      return;
    }

    if (!patientDetails?.id || isPatientProfileIncomplete(patientDetails)) {
      persistPendingBooking();
      openedProfileForBookingRef.current = true;
      setShowAddProfile(true);
      toast.info("Please complete your profile, then choose Book Appointment to confirm.");
      return;
    }

    if (!appointmentDate || !appointmentTime) {
      toast.error("Please select both date and time.");
      return;
    }

    if (!isLabTestBooking && !paymentMethod) {
      toast.error("Please select a payment method.");
      return;
    }

    setSubmitting(true);
    try {
      const slotTime = parseSlotTime(appointmentDate, appointmentTime);
      const formattedTime = slotTime.format("HH:mm");
      const appointmentDateTime = `${dayjs(appointmentDate).format("YYYY-MM-DD")}T${formattedTime}`;
      const rescheduleTarget = rescheduleTargetRef.current;
      const rescheduleId = getAppointmentIdFromEnriched(rescheduleTarget);

      if (isRescheduling && rescheduleId) {
        if (isAppointmentCompleted(rescheduleTarget)) {
          toast.error("Completed appointments cannot be rescheduled. Please create a new appointment.");
          setIsRescheduling(false);
          rescheduleTargetRef.current = null;
          return;
        }

        await updateAppointment(
          rescheduleId,
          buildPatientAppointmentUpdatePayload({
            patientId: patientDetails.id,
            doctorId,
            appointmentDateTime,
            isVirtual,
            existingData: rescheduleTarget,
          })
        );

        try {
          await syncPatientTypeForWorkflow(patientDetails.id, "OUT", { force: true });
        } catch (syncErr) {
          console.error("syncPatientTypeForWorkflow failed after reschedule:", syncErr);
        }

        rescheduleAppointmentReminders({
          appointmentId: rescheduleId,
          appointmentDateTime,
          patientId: patientDetails.id,
          doctorId,
          isVirtual: isVirtual || false,
          patientMobile: patientDetails?.mobile || null,
          patientName: patientDetails?.name || null,
          doctorMobile: doctor?.mobile || null,
          doctorName: doctor?.name || null,
          departmentName: doctor?.departmentName || doctor?.department_name || null,
        }).catch((error) => {
          console.error("Failed to reschedule appointment reminders:", error);
        });

        toast.success("Appointment rescheduled successfully.");
        setIsRescheduling(false);
        rescheduleTargetRef.current = null;
        allowAdditionalSameDayAppointmentRef.current = false;
        clearPendingAppointment();
        clearBookingOrganizationId();
        navigate("/patient");
        return;
      }

      if (isLabTestBooking) {
        const tests = await ensurePrescribedTests();
        await bookPrescribedLabTests({
          patientId: patientDetails.id,
          prescriptionId: prescriptionIdFromUrl,
          labTestIds: tests.map((test) => test.labTestId),
          preferredDate: dayjs(appointmentDate).format("YYYY-MM-DD"),
          preferredTime: appointmentTime,
          doctorId,
          notes: `Patient portal test booking on ${dayjs(appointmentDate).format("YYYY-MM-DD")} at ${appointmentTime}`,
        });
        toast.success(`${tests.length} prescribed test(s) booked successfully.`);
        clearPendingAppointment();
        clearBookingOrganizationId();
        navigate("/patient");
        return;
      }

      if (!allowAdditionalSameDayAppointmentRef.current) {
        const existingAppt = await lookupSameDoctorDayConflict(appointmentDateTime);
        if (existingAppt) {
          openExistingAppointmentDialog(existingAppt, appointmentDateTime);
          return;
        }
      }

      const appointmentData = {
        patientId: patientDetails.id,
        doctorId: doctorId,
        appointmentDateTime,
        isVirtual: isVirtual,
        paymentMethod: paymentMethod === "pay-at-hospital" ? "PAY_AT_HOSPITAL" : "PAID_ONLINE",
        status: paymentMethod === "pay-at-hospital" ? "PENDING_CONFIRMATION" : "CONFIRMED",
        organizationId: bookingOrganizationId,
      };

      const createdAppointment = await createPatientPortalAppointment(appointmentData);

      await syncPatientTypeForWorkflow(patientDetails.id, "OUT", { force: true });

      if (createdAppointment?.id || createdAppointment?.appointmentId) {
        scheduleRemindersForAppointment(
          {
            ...createdAppointment,
            appointmentDateTime,
            patientId: patientDetails.id,
            doctorId: doctorId,
            isVirtual: isVirtual || false,
            virtualLink:
              createdAppointment?.virtualLink ||
              createdAppointment?.virtual_link ||
              createdAppointment?.virtualMeeting?.url ||
              null,
          },
          {
            patient: patientDetails,
            doctor: doctor,
          }
        ).catch((error) => {
          console.error("Failed to schedule appointment reminders:", error);
        });
      }

      // Keep the booking hospital until invoice create finishes, then clear
      // the login/profile hand-off so it is not replayed on the dashboard.
      clearPendingAppointment();

      if (paymentMethod === "pay-online") {
        try {
          const appointmentAmount = getConsultationFee(doctor);
          const invoiceData = {
            patientId: patientDetails.id,
            doctorId:
              doctorId != null && doctorId !== ""
                ? Number(doctorId)
                : doctor?.id != null
                  ? Number(doctor.id)
                  : undefined,
            departmentId:
              doctor?.departmentId || doctor?.department_id
                ? Number(doctor.departmentId || doctor.department_id)
                : undefined,
            amount: appointmentAmount,
            sgst: appointmentAmount * 0.085,
            cgst: appointmentAmount * 0.085,
            totalAmount: appointmentAmount * 1.17,
            invoiceType: "OPD",
            date: dayjs().format("YYYY-MM-DDTHH:mm"),
            paymentMode: "ONLINE",
            billType: "OPD",
            appointmentId: createdAppointment.id,
            userId: user?.id || undefined,
            organizationId: bookingOrganizationId,
          };

          const invoice = await createInvoice(invoiceData);

          if (invoice?.id) {
            toast.success("Payment processed successfully! Appointment confirmed.");
          }
        } catch (invoiceError) {
          console.error("Error creating invoice:", invoiceError);
          toast.error("Appointment created but payment processing failed. Please contact support.");
        }
      } else {
        toast.success("Appointment request submitted! Please visit the front desk for confirmation and payment.");
      }

      allowAdditionalSameDayAppointmentRef.current = false;
      clearBookingOrganizationId();
      navigate("/patient");
    } catch (err) {
      console.error(isLabTestBooking ? "Error booking test:" : "Error booking appointment:", err);
      if (!isLabTestBooking && isAlreadyBookedAtSelectedTimeError(err)) {
        const slotTime = parseSlotTime(appointmentDate, appointmentTime);
        const formattedTime = slotTime.format("HH:mm");
        const appointmentDateTime = `${dayjs(appointmentDate).format("YYYY-MM-DD")}T${formattedTime}`;
        const existingAppt = await lookupSameDoctorDayConflict(appointmentDateTime);
        openExistingAppointmentDialog(
          existingAppt || { appointmentDateTime },
          appointmentDateTime
        );
        return;
      }
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          (isLabTestBooking
            ? "Failed to book test. Please try again."
            : "Failed to book appointment. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getAvailableTimeSlots = (date = appointmentDate, occupancy = existingAppointments) => {
    const timings = isLabTestBooking ? getPathologyDeskSlots() : getScheduleTimings(doctor, date);
    if (!timings.length) return [];

    const selectedDate = dayjs(date);
    const isToday = selectedDate.isSame(dayjs(), "day");

    return timings.map((time) => {
      const slotTime = parseSlotTime(selectedDate, time);
      const isBooked =
        !isLabTestBooking &&
        occupancy.some(
          (appointment) =>
            dayjs(appointment.appointmentDateTime).format("HH:mm") === slotTime.format("HH:mm")
        );
      const isDisabled = isToday && slotTime.isBefore(dayjs());

      return {
        time,
        isBooked,
        isDisabled,
      };
    });
  };

  const dateOptions = useMemo(() => {
    if (!doctor) return [];
    const options = [];
    for (let i = 0; i < 8; i += 1) {
      const date = dayjs().add(i, "day");
      const slots = getAvailableTimeSlots(date, i === 0 ? existingAppointments : []);
      const available = slots.filter((slot) => !slot.isBooked && !slot.isDisabled);
      options.push({
        date,
        count: available.length,
      });
    }
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctor, existingAppointments]);

  const nextSlots = useMemo(() => {
    const firstWithSlots = dateOptions.find((option) => option.count > 0);
    if (!firstWithSlots) return { inHospital: null, video: null };
    const slots = getAvailableTimeSlots(
      firstWithSlots.date,
      firstWithSlots.date.isSame(dayjs(), "day") ? existingAppointments : []
    );
    const firstOpen = slots.find((slot) => !slot.isBooked && !slot.isDisabled);
    if (!firstOpen) return { inHospital: null, video: null };
    const label = formatNextSlotLabel(firstWithSlots.date, firstOpen.time);
    return { inHospital: label, video: label };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, existingAppointments]);

  const leaveDoctorProfile = () => {
    const pending = readPendingAppointment();
    if (!pending?.confirmOnReturn) {
      clearPendingAppointment();
    }
    navigate(user?.id ? "/patient" : "/landing");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!doctor) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Doctor not found.</Alert>
        <Button onClick={leaveDoctorProfile} sx={{ mt: 2 }}>
          {user?.id ? "Back to Dashboard" : "Back"}
        </Button>
      </Box>
    );
  }

  const timeSlots = getAvailableTimeSlots();
  const openSlots = timeSlots.filter((slot) => !slot.isBooked && !slot.isDisabled);

  const groupedSlots = timeSlots.reduce(
    (groups, slot) => {
      const hour = parseSlotTime(appointmentDate, slot.time).hour();
      if (hour < 12) groups.morning.push(slot);
      else if (hour < 16) groups.afternoon.push(slot);
      else groups.evening.push(slot);
      return groups;
    },
    { morning: [], afternoon: [], evening: [] }
  );

  const getDepartmentName = () => {
    if (!doctor) return "General Medicine";
    if (doctor.departmentName) return doctor.departmentName;
    if (doctor.department?.name) return doctor.department.name;
    if (doctor.department && typeof doctor.department === "string") return doctor.department;
    if (doctor.departmentId && departments.length > 0) {
      const dept = departments.find(
        (d) => d.id === doctor.departmentId || String(d.id) === String(doctor.departmentId)
      );
      if (dept) return dept.name || dept.departmentName || "General Medicine";
    }
    if (Array.isArray(doctor.departmentIds) && doctor.departmentIds.length > 0 && departments.length > 0) {
      const deptId = doctor.departmentIds[0];
      const dept = departments.find((d) => d.id === deptId || String(d.id) === String(deptId));
      if (dept) return dept.name || dept.departmentName || "General Medicine";
    }
    return doctor.specialty || "General Medicine";
  };

  const departmentName = getDepartmentName();
  const qualification = isLabTestBooking
    ? "Sample collection, prescribed lab tests, and lab reports"
    : doctor.qualification || doctor.education || doctor.specialty || "";
  const doctorName = formatDoctorName(doctor.name);
  const pathDeskName =
    pathDesk?.name ||
    pathDesk?.fullName ||
    pathDesk?.full_name ||
    pathDesk?.staffName ||
    [pathDesk?.firstName, pathDesk?.lastName].filter(Boolean).join(" ") ||
    "Pathology Desk";
  const profileName = isLabTestBooking ? pathDeskName : doctorName;
  const profileSubtitle = isLabTestBooking
    ? "Pathology Desk"
    : doctor.designation || `Attending Consultant - ${departmentName}`;
  const photo = isLabTestBooking ? getDoctorPhoto(pathDesk) : getDoctorPhoto(doctor);
  const fee = getConsultationFee(doctor);
  const experienceLabel = isLabTestBooking ? "" : getExperienceLabel(doctor);
  const selectedHospital =
    hospitals.find(
      (org) => String(org?.id ?? org?.organizationId ?? org?.organization_id) === String(selectedHospitalId)
    ) || null;
  const hospitalLabel = selectedHospital
    ? getHospitalName(selectedHospital)
    : doctor.location || doctor.address || "Hospital";
  const hospitalPhone =
    getHospitalPhone(selectedHospital) ||
    getHospitalPhone(doctor) ||
    "";
  const selectedDateChipLabel = getDateChipLabel(selectedCalendarDate);
  const noSlotsMessage = hospitalPhone
    ? `No slots available. Please place a request or feel free to call us at ${hospitalPhone} to schedule an appointment.`
    : "No slots available. Please place a request or feel free to call us at the hospital to schedule an appointment.";

  const hospitalChoices = hospitals.filter((org) => {
    const orgId = org?.id ?? org?.organizationId ?? org?.organization_id;
    if (selectedHospitalId) return String(orgId) === String(selectedHospitalId);
    return true;
  });
  const hospitalMenu = hospitalChoices.length ? hospitalChoices : hospitals;

  const aboutHighlights = isLabTestBooking
    ? [
        "Collects samples for the lab tests you book and records them against your prescription.",
        "Keeps the lab queue, tracks each booked test, and updates its status until the report is ready.",
        "Prepares lab reports for the tests you book and makes them available on your patient dashboard.",
      ]
    : [
    doctor.aboutHighlight1 ||
      `Expert in diagnosing and managing ${departmentName.toLowerCase()} conditions through evidence-based, patient-centred care.`,
    doctor.aboutHighlight2 ||
      (qualification
        ? `${qualification} with experience in managing acute and chronic medical conditions.`
        : `Fellowship-trained clinician with experience in managing critically ill and complex cases.`),
    doctor.aboutHighlight3 ||
      `Special interest in preventive healthcare, lifestyle modification, and long-term management of chronic diseases.`,
  ];

  const aboutParagraph = isLabTestBooking
    ? `${pathDeskName} is the pathology desk at ${hospitalLabel}. This desk looks after sample collection, prescribed lab tests, and lab reports. Its collection slots are every hour from 9:00 AM to 5:00 PM. You select an available slot on this profile, and you book your test.`
    : doctor.about ||
      doctor.bio ||
      doctor.description ||
      `${doctorName} is a ${departmentName} specialist with expertise in the diagnosis, treatment, and prevention of a broad spectrum of adult medical conditions.`;

  const ctaLabel = !user?.id
    ? "Login to Select Patient"
    : isLabTestBooking
      ? "Book Test"
      : isRescheduling
        ? "Update Appointment"
        : paymentMethod === "pay-online"
          ? "Pay & Book Appointment"
          : "Book Appointment";

  const handleSelectDate = (date) => {
    if (date && date.isAfter(dayjs().subtract(1, "day"))) {
      setSelectedCalendarDate(date);
      setHospitalExpanded(false);
      setAppointmentTime("");
    }
  };

  const scrollDateStrip = (direction) => {
    if (!dateStripRef.current) return;
    dateStripRef.current.scrollBy({
      left: direction * 180,
      behavior: "smooth",
    });
  };

  const slotButtonSx = (slot) => ({
    minWidth: 0,
    width: "100%",
    height: 40,
    borderRadius: "20px",
    fontSize: "0.82rem",
    fontWeight: 600,
    textTransform: "none",
    px: 0.5,
    borderColor: slot.isBooked || slot.isDisabled ? BORDER : ACCENT,
    backgroundColor:
      slot.isDisabled || slot.isBooked
        ? "#F3F4F6"
        : appointmentTime === slot.time
          ? ACCENT
          : "#fff",
    color:
      slot.isDisabled || slot.isBooked
        ? "#9CA3AF"
        : appointmentTime === slot.time
          ? "#fff"
          : ACCENT,
    "&:hover": {
      borderColor: ACCENT_DARK,
      backgroundColor:
        slot.isDisabled || slot.isBooked
          ? "#F3F4F6"
          : appointmentTime === slot.time
            ? ACCENT_DARK
            : BLUE_LIGHT,
    },
  });

  const renderSlotGroup = (label, caption, slots, icon) => {
    if (!slots.length) return null;
    return (
      <Box key={label} sx={{ mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25, color: TEXT_GREY }}>
          {icon}
          <Typography sx={{ fontSize: 13, color: TEXT_GREY }}>
            {label}
            {caption ? ` | ${caption}` : ""}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1,
            maxHeight: 92,
            overflowY: "auto",
            pr: 0.25,
            "&::-webkit-scrollbar": { height: 6, width: 6 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#B7D7F2", borderRadius: 8 },
          }}
        >
          {slots.map((slot, index) => (
            <Button
              key={`${label}-${index}`}
              variant={appointmentTime === slot.time ? "contained" : "outlined"}
              onClick={() => {
                if (!slot.isBooked && !slot.isDisabled) {
                  setAppointmentTime(slot.time);
                }
              }}
              disabled={slot.isBooked || slot.isDisabled}
              sx={slotButtonSx(slot)}
            >
              {slot.time}
            </Button>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#fff", color: TEXT_DARK }}>
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          overflow: "visible",
          backgroundColor: HERO_BG,
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 5 },
          px: { xs: 0, md: 0 },
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 3, md: 6, lg: 10 } }}>
          <Box
            sx={{
              pr: 0,
              "@media (min-width: 900px)": {
                pr: "440px",
              },
            }}
          >
            <Box sx={{ display: "flex", gap: { xs: 2, md: 3.5 }, alignItems: "flex-start" }}>
                <IconButton
                  onClick={leaveDoctorProfile}
                  sx={{
                    mt: 1.5,
                    border: `1px solid ${BORDER}`,
                    backgroundColor: "#fff",
                    flexShrink: 0,
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box
                  sx={{
                    width: { xs: 132, sm: 176 },
                    height: { xs: 132, sm: 176 },
                    borderRadius: "50%",
                    border: "2px dotted #C5CAD3",
                    p: "6px",
                    flexShrink: 0,
                    boxSizing: "border-box",
                  }}
                >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    border: `8px solid ${ACCENT}`,
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  <Avatar
                    src={photo || undefined}
                    alt={profileName}
                    sx={{
                      width: "100%",
                      height: "100%",
                      bgcolor: BLUE_LIGHT,
                      color: BLUE,
                      fontSize: { xs: 42, md: 64 },
                      fontWeight: 700,
                    }}
                  >
                    {isLabTestBooking
                      ? pathDeskName.charAt(0)?.toUpperCase() || "P"
                      : stripDoctorPrefix(doctor.name).charAt(0)?.toUpperCase() || "D"}
                  </Avatar>
                </Box>
                </Box>
                <Box sx={{ pt: { xs: 1, md: 2.5 }, minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 800, lineHeight: 1.15 }}>
                    {profileName}
                  </Typography>
                  <Typography sx={{ mt: 0.75, fontSize: 16, color: TEXT_DARK }}>
                    {profileSubtitle}
                    {isLabTestBooking ? ` • ${hospitalLabel}` : ""}
                  </Typography>
                  {qualification && (
                    <Typography sx={{ mt: 0.75, fontSize: 14.5, color: TEXT_GREY, lineHeight: 1.5 }}>
                      {String(qualification).replace(/\s*\|\s*/g, " | ")}
                    </Typography>
                  )}
                  {experienceLabel && (
                    <Typography sx={{ mt: 1, fontSize: 14.5, color: TEXT_DARK }}>
                      {experienceLabel}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      my: 2,
                      width: "100%",
                      borderBottom: "1.5px dotted #B0B6C0",
                    }}
                  />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>Next Slot:</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ maxWidth: "100%" }}>
                    {nextSlots.inHospital && (
                      <ChipLike
                        icon={<HomeOutlinedIcon sx={{ fontSize: 16, color: BLUE }} />}
                        label={nextSlots.inHospital}
                      />
                    )}
                    {!isLabTestBooking && nextSlots.video && (
                      <ChipLike
                        icon={<VideocamOutlinedIcon sx={{ fontSize: 16, color: TEXT_GREY }} />}
                        label={nextSlots.video}
                      />
                    )}
                    {!nextSlots.inHospital && !nextSlots.video && (
                      <ChipLike label="No slots" />
                    )}
                  </Stack>
                </Box>
              </Box>
          </Box>

            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 400,
                mt: { xs: 3, md: 0 },
                mx: { xs: "auto", md: 0 },
                borderRadius: "16px",
                backgroundColor: "#fff",
                boxShadow: "0 12px 40px rgba(47,58,74,0.12)",
                border: `1px solid ${BORDER}`,
                zIndex: 3,
                overflow: "hidden",
                "@media (min-width: 900px)": {
                  position: "absolute",
                  top: 48,
                  right: 80,
                },
              }}
            >
              {isLabTestBooking && (
                <Alert
                  severity={prescriptionError ? "error" : "info"}
                  sx={{ borderRadius: 0 }}
                >
                  {prescriptionError
                    ? prescriptionError
                    : !prescriptionReady
                      ? "Checking prescription..."
                      : `Prescription #${prescriptionIdFromUrl}: ${
                          prescribedTests.map((test) => test.testName).join(", ") || "prescribed tests"
                        }. Select this path desk's available slot, then book your test.`}
                </Alert>
              )}
              <Box sx={{ display: "flex" }}>
                <Button
                  fullWidth
                  disabled={appointmentTypePreSelected && !isInPersonFromUrl && isVirtualFromUrl}
                  onClick={() => {
                    if (appointmentTypePreSelected && isVirtualFromUrl) return;
                    setIsVirtual(false);
                    setPaymentMethod("pay-at-hospital");
                    setAppointmentTime("");
                  }}
                  sx={{
                    py: 1.4,
                    borderRadius: 0,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 15,
                    color: !isVirtual ? "#fff" : TEXT_GREY,
                    backgroundColor: !isVirtual ? ACCENT : "#fff",
                    "&:hover": { backgroundColor: !isVirtual ? ACCENT_DARK : "#FAFAFA" },
                  }}
                >
                  In-Hospital
                </Button>
                <Button
                  fullWidth
                  disabled={appointmentTypePreSelected && isInPersonFromUrl}
                  onClick={() => {
                    if (appointmentTypePreSelected && isInPersonFromUrl) return;
                    setIsVirtual(true);
                    setPaymentMethod("pay-online");
                    setAppointmentTime("");
                  }}
                  sx={{
                    py: 1.4,
                    borderRadius: 0,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 15,
                    color: isVirtual ? "#fff" : TEXT_GREY,
                    backgroundColor: isVirtual ? ACCENT : "#fff",
                    "&:hover": { backgroundColor: isVirtual ? ACCENT_DARK : "#FAFAFA" },
                  }}
                >
                  Video
                </Button>
              </Box>

              <Box
                sx={{
                  position: "relative",
                  backgroundColor: "#F3F4F6",
                  py: 1,
                  px: 2,
                }}
              >
                  <IconButton
                    size="small"
                    onClick={() => scrollDateStrip(-1)}
                    sx={{
                      position: "absolute",
                      left: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 2,
                      width: 28,
                      height: 28,
                      border: `1px solid ${ACCENT}`,
                      backgroundColor: "#fff",
                      color: ACCENT,
                      boxShadow: "0 1px 4px rgba(47,58,74,0.12)",
                      "&:hover": { backgroundColor: BLUE_LIGHT },
                    }}
                  >
                    <KeyboardArrowLeftIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Box
                    ref={dateStripRef}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      overflowX: "auto",
                      px: 3,
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                    }}
                  >
                    {dateOptions.map((option, index) => {
                        const selected = selectedCalendarDate.isSame(option.date, "day");
                        return (
                          <React.Fragment key={option.date.format("YYYY-MM-DD")}>
                            {index > 0 && (
                              <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                  mx: 1.5,
                                  my: 0.75,
                                  borderColor: BORDER,
                                }}
                              />
                            )}
                            <Button
                              onClick={() => handleSelectDate(option.date)}
                              sx={{
                                minWidth: 72,
                                px: 0.75,
                                py: 0.75,
                                borderRadius: 0,
                                borderBottom: selected ? `3px solid ${ACCENT}` : "3px solid transparent",
                                color: selected ? ACCENT : TEXT_GREY,
                                textTransform: "none",
                                fontWeight: 700,
                                lineHeight: 1.2,
                                flexShrink: 0,
                              }}
                            >
                              <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "inherit" }}>
                                  {getDateChipLabel(option.date)}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    fontWeight: 500,
                                    color: selected ? ACCENT : TEXT_GREY,
                                  }}
                                >
                                  {option.count > 0 ? `${option.count} Slots` : "No Slots"}
                                </Typography>
                              </Box>
                            </Button>
                          </React.Fragment>
                        );
                      })}
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        mx: 1.5,
                        my: 0.75,
                        borderColor: BORDER,
                      }}
                    />
                    <Button
                      onClick={(event) => setCalendarAnchor(event.currentTarget)}
                      sx={{
                        minWidth: 88,
                        px: 0.75,
                        py: 0.75,
                        borderRadius: 0,
                        borderBottom: dateOptions.some((option) =>
                          option.date.isSame(selectedCalendarDate, "day")
                        )
                          ? "3px solid transparent"
                          : `3px solid ${ACCENT}`,
                        color: dateOptions.some((option) =>
                          option.date.isSame(selectedCalendarDate, "day")
                        )
                          ? TEXT_DARK
                          : ACCENT,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 13,
                        lineHeight: 1.2,
                        flexShrink: 0,
                      }}
                    >
                      Custom Date
                    </Button>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => scrollDateStrip(1)}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 2,
                      width: 28,
                      height: 28,
                      border: `1px solid ${ACCENT}`,
                      backgroundColor: "#fff",
                      color: ACCENT,
                      boxShadow: "0 1px 4px rgba(47,58,74,0.12)",
                      "&:hover": { backgroundColor: BLUE_LIGHT },
                    }}
                  >
                    <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
              </Box>

              <Box sx={{ p: 2 }}>
                <Box
                  onClick={() => setHospitalExpanded((open) => !open)}
                  sx={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: "10px",
                    p: 1.25,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      backgroundColor: BLUE_LIGHT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isVirtual ? (
                      <YouTubeIcon sx={{ color: BLUE, fontSize: 22 }} />
                    ) : (
                      <HomeOutlinedIcon sx={{ color: BLUE, fontSize: 20 }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {isVirtual ? (
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>
                        Video Consultation
                      </Typography>
                    ) : hospitalMenu.length > 1 ? (
                      <Select
                        size="small"
                        variant="standard"
                        disableUnderline
                        value={selectedHospitalId}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => setSelectedHospitalId(event.target.value)}
                        sx={{ fontWeight: 700, fontSize: 14, width: "100%" }}
                      >
                        {hospitalMenu.map((org) => {
                          const orgId = org?.id ?? org?.organizationId ?? org?.organization_id;
                          return (
                            <MenuItem key={orgId} value={String(orgId)}>
                              {getHospitalName(org)}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    ) : (
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>
                        {hospitalLabel}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 12, color: TEXT_GREY }} noWrap>
                      {openSlots.length > 0
                        ? isLabTestBooking
                          ? `${openSlots.length} Slots Available ${selectedDateChipLabel}`
                          : `${openSlots.length} Slots Available ${selectedDateChipLabel} | ₹${fee}`
                        : isLabTestBooking
                          ? `0 Slots Available ${selectedDateChipLabel}`
                          : `0 Slots Available ${selectedDateChipLabel} | ₹${fee}`}
                    </Typography>
                  </Box>
                  {hospitalExpanded ? (
                    <KeyboardArrowUpIcon sx={{ color: TEXT_GREY }} />
                  ) : (
                    <KeyboardArrowDownIcon sx={{ color: TEXT_GREY }} />
                  )}
                </Box>

                {openSlots.length === 0 ? (
                  hospitalExpanded && (
                    <Typography sx={{ mt: 2, fontSize: 13.5, color: TEXT_GREY, lineHeight: 1.65 }}>
                      {noSlotsMessage}
                    </Typography>
                  )
                ) : (
                  <>
                    <Collapse in={hospitalExpanded}>
                      <Box sx={{ pt: 2 }}>
                        {renderSlotGroup(
                          "Morning",
                          groupedSlots.morning.length
                            ? `Till 12PM (${groupedSlots.morning.filter((s) => !s.isBooked && !s.isDisabled).length} Slots)`
                            : "",
                          groupedSlots.morning,
                          <WbSunnyOutlinedIcon sx={{ fontSize: 16 }} />
                        )}
                        {renderSlotGroup(
                          "Afternoon",
                          groupedSlots.afternoon.length
                            ? `12PM - 4PM (${groupedSlots.afternoon.filter((s) => !s.isBooked && !s.isDisabled).length} Slots)`
                            : "",
                          groupedSlots.afternoon,
                          <WbSunnyOutlinedIcon sx={{ fontSize: 16 }} />
                        )}
                        {renderSlotGroup(
                          "Evening",
                          groupedSlots.evening.length
                            ? `After 4PM (${groupedSlots.evening.filter((s) => !s.isBooked && !s.isDisabled).length} Slots)`
                            : "",
                          groupedSlots.evening,
                          <NightsStayOutlinedIcon sx={{ fontSize: 16 }} />
                        )}
                      </Box>
                    </Collapse>

                    {appointmentTime && user?.id && !isLabTestBooking && (
                      <Box sx={{ mt: 2 }}>
                        <FormLabel component="legend" sx={{ mb: 1, fontSize: "0.9rem", fontWeight: 600 }}>
                          Payment Method
                        </FormLabel>
                        <RadioGroup
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        >
                          <FormControlLabel
                            value="pay-at-hospital"
                            control={<Radio size="small" disabled={isVirtual} />}
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <LocalHospitalIcon fontSize="small" />
                                <Typography variant="body2" sx={{ color: isVirtual ? "text.disabled" : "inherit" }}>
                                  Pay at Hospital
                                </Typography>
                              </Box>
                            }
                            disabled={isVirtual}
                          />
                          <FormControlLabel
                            value="pay-online"
                            control={<Radio size="small" />}
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <PaymentIcon fontSize="small" />
                                <Typography variant="body2">Pay Here (Online/UPI)</Typography>
                              </Box>
                            }
                          />
                        </RadioGroup>
                        {isVirtual && (
                          <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: "0.75rem" }}>
                            Virtual appointments require online payment only.
                          </Alert>
                        )}
                      </Box>
                    )}

                    {appointmentTime && (
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={handleBookAppointment}
                        disabled={
                          submitting ||
                          (Boolean(user?.id) && !isLabTestBooking && !paymentMethod) ||
                          (isLabTestBooking &&
                            Boolean(user?.id) &&
                            (!prescriptionReady || Boolean(prescriptionError)))
                        }
                        sx={{
                          mt: 2,
                          py: 1.4,
                          fontSize: "0.98rem",
                          fontWeight: 700,
                          textTransform: "none",
                          backgroundColor: ACCENT,
                          boxShadow: "none",
                          "&:hover": { backgroundColor: ACCENT_DARK, boxShadow: "none" },
                        }}
                      >
                        {submitting ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : ctaLabel}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Paper>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: "#fff", position: "relative", zIndex: 1, py: { xs: 4, md: 6 } }}>
        <Container maxWidth={false} sx={{ px: { xs: 3, md: 6, lg: 10 } }}>
          <Box
            sx={{
              pr: 0,
              "@media (min-width: 900px)": {
                pr: "440px",
              },
            }}
          >
            <Typography sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800, mb: 3, color: TEXT_DARK }}>
              About {profileName}
            </Typography>
            <Grid container spacing={3}>
              {aboutHighlights.map((text) => (
                <Grid item xs={12} sm={4} key={text}>
                  <Typography sx={{ fontSize: 15.5, lineHeight: 1.7, color: TEXT_DARK }}>{text}</Typography>
                </Grid>
              ))}
            </Grid>
            <Typography sx={{ mt: 3, fontSize: 15, lineHeight: 1.8, color: TEXT_GREY }}>
              {aboutParagraph}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            value={selectedCalendarDate}
            onChange={(newValue) => {
              handleSelectDate(newValue);
              setCalendarAnchor(null);
            }}
            minDate={dayjs()}
            sx={{
              "& .Mui-selected": {
                backgroundColor: `${ACCENT} !important`,
              },
            }}
          />
        </LocalizationProvider>
      </Popover>

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onLoginSuccess={() => {
            setShowLogin(false);
            try {
              const storedUser = JSON.parse(localStorage.getItem("user") || "null");
              applyPatientBookingOrganization(
                storedUser,
                bookingOrganizationId || getBookingOrganizationId()
              );
            } catch (error) {
              console.warn("Could not apply hospital organization after login:", error);
            }
          }}
          onOtpVerified={(verifiedNumber) => {
            setShowLogin(false);
            setVerifiedMobile(verifiedNumber);
          }}
        />
      )}

      {(verifiedMobile || showAddProfile) && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            overflowY: "auto",
          }}
        >
          <PatientAddProfile
            user={user}
            existingPatient={patientDetails}
            verifiedMobile={
              verifiedMobile ||
              String(user?.mobileNumber || user?.username || patientDetails?.mobile || "")
                .replace(/\D/g, "")
                .slice(-10)
            }
            organizationId={bookingOrganizationId}
            onBack={() => {
              openedProfileForBookingRef.current = false;
              setVerifiedMobile("");
              setShowAddProfile(false);
            }}
            onCompleted={async () => {
              const stayToConfirmBooking = openedProfileForBookingRef.current;
              openedProfileForBookingRef.current = false;
              setVerifiedMobile("");
              setShowAddProfile(false);
              if (stayToConfirmBooking) {
                if (user?.id) {
                  try {
                    const patientData = await getPatientByUserId(user.id, {
                      mobile: user.mobileNumber || user.username,
                      organizationId: bookingOrganizationId,
                    });
                    if (patientData?.id) {
                      setPatientDetails(patientData);
                    }
                  } catch (refreshError) {
                    console.warn("Could not refresh patient profile after create:", refreshError);
                  }
                }
                return;
              }
              const pending = readPendingAppointment();
              if (pending?.doctorId && pending.appointmentDate && pending.appointmentTime) {
                savePendingAppointment({ ...pending, confirmOnReturn: true });
              }
              navigate("/patient");
            }}
          />
        </Box>
      )}

      <Dialog
        open={confirmationDialog.open}
        onClose={closeConfirmationDialog}
        aria-labelledby="patient-existing-appointment-title"
        aria-describedby="patient-existing-appointment-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="patient-existing-appointment-title">
          Existing Appointment Found
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="patient-existing-appointment-description"
            sx={{ color: TEXT_DARK }}
          >
            You already have a pending appointment on{" "}
            {(() => {
              const raw =
                getRawAppointmentDateTime(confirmationDialog.existingAppointment) ||
                confirmationDialog.appointmentDateTime;
              return raw && dayjs(raw).isValid()
                ? dayjs(raw).format("DD MMM YYYY, hh:mm A")
                : dayjs(appointmentDate).format("DD MMM YYYY");
            })()}
            .
            <br />
            <br />
            Do you want to create a new appointment or reschedule?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, px: 2, pb: 2, flexWrap: "wrap" }}>
          <Button onClick={closeConfirmationDialog} variant="outlined" color="primary">
            Cancel
          </Button>
          <Button onClick={handleCreateNewAppointmentChoice} color="primary" variant="outlined">
            Create New Appointment
          </Button>
          <Button onClick={handleRescheduleAppointment} color="primary" variant="contained">
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ChipLike = ({ icon, label }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.25,
      py: 0.55,
      borderRadius: "8px",
      border: `1px solid ${BORDER}`,
      backgroundColor: "#fff",
      fontSize: 13,
      color: TEXT_DARK,
    }}
  >
    {icon}
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "inherit" }}>{label}</Typography>
  </Box>
);

export default DoctorProfile;
